<template>
  <div class="dept-editor">
    <div v-for="(row, i) in value" :key="i" class="dept-row" :class="{ 'is-main': isMain(row) }">
      <v-btn
        icon
        class="dept-row__star"
        :aria-label="isMain(row) ? 'Departament principal' : 'Faceți-l departament principal'"
        :title="isMain(row) ? 'Departament principal' : 'Faceți-l departament principal'"
        :disabled="!row.department_id"
        @click="$emit('update:mainId', row.department_id)"
      >
        <v-icon>{{ isMain(row) ? "mdi-star" : "mdi-star-outline" }}</v-icon>
      </v-btn>
      <v-autocomplete
        v-model="row.department_id"
        :items="optionsFor(row)"
        item-text="name"
        item-value="id"
        :label="i === 0 ? 'Departament*' : 'Alt departament'"
        prepend-inner-icon="mdi-domain"
        outlined
        dense
        hide-details
        class="dept-row__select"
        @change="changed(row)"
      >
        <!-- a membership of a department hidden in Configurare stays, marked -->
        <template #selection="{ item }">
          <span class="dept-row__name">{{ item.name }}</span><span v-if="item.hidden" class="tag-hidden">ascuns</span>
        </template>
        <template #item="{ item }">
          <v-list-item-content>
            <v-list-item-title>
              {{ item.name }}<span v-if="item.hidden" class="tag-hidden">ascuns</span>
            </v-list-item-title>
          </v-list-item-content>
        </template>
      </v-autocomplete>
      <v-switch
        v-if="allowSef"
        v-model="row.is_sef"
        label="Șef"
        inset
        dense
        hide-details
        color="primary"
        class="dept-row__sef"
        @change="$emit('input', value)"
      ></v-switch>
      <v-btn
        icon
        aria-label="Scoate departamentul"
        title="Scoate departamentul"
        :disabled="value.length === 1"
        @click="remove(i)"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    <div v-if="error" class="dept-editor__error">{{ error }}</div>
    <div class="dept-editor__foot">
      <v-btn text color="primary" @click="add">
        <v-icon left>mdi-plus</v-icon>Încă un departament
      </v-btn>
      <span class="dept-editor__hint">
        <v-icon small>mdi-star</v-icon> departamentul principal.
        <template v-if="allowSef">«Șef» = aprobă demersurile acelui departament.</template>
      </span>
    </div>
  </div>
</template>

<script>
// Departments of a user (v-model: [{ department_id, is_sef }]), with the main one chosen by the star (mainId.sync)
export default {
  name: "DepartmentsEditor",
  props: {
    value: { type: Array, required: true },
    mainId: { type: Number, default: null },
    departments: { type: Array, default: () => [] }, // [{ id, name, hidden? }]
    allowSef: { type: Boolean, default: false },
    error: { type: String, default: "" },
  },
  methods: {
    isMain(row) {
      return !!row.department_id && row.department_id === this.mainId;
    },
    // a department can be picked only once
    optionsFor(row) {
      const taken = this.value.filter((r) => r !== row).map((r) => r.department_id);
      return this.departments.filter((d) => !taken.includes(d.id));
    },
    changed(row) {
      if (!this.mainId || !this.value.some((r) => r.department_id === this.mainId)) {
        this.$emit("update:mainId", row.department_id);
      }
      this.$emit("input", this.value);
    },
    add() {
      this.$emit("input", [...this.value, { department_id: null, is_sef: false }]);
    },
    remove(i) {
      const next = this.value.filter((_, index) => index !== i);
      this.$emit("input", next);
      if (!next.some((r) => r.department_id === this.mainId)) {
        this.$emit("update:mainId", next[0] ? next[0].department_id : null);
      }
    },
  },
};
</script>

<style scoped>
.dept-editor {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dept-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 4px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
}
.dept-row.is-main {
  border-color: var(--green);
  background: var(--mint-50);
}
.dept-row__star .v-icon {
  color: var(--ink-3);
}
.dept-row.is-main .dept-row__star .v-icon {
  color: #d08a12;
}
.dept-row__select {
  flex: 1 1 auto;
  min-width: 0;
}
.dept-row__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tag-hidden {
  flex: none;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--slate-bg);
  color: var(--slate);
  font-size: 0.75rem;
  font-weight: 700;
}
.dept-row__sef {
  flex: none;
  margin: 0 4px 0 6px;
  padding: 0;
}
.dept-editor__error {
  color: var(--brick);
  font-size: 0.9rem;
}
.dept-editor__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 16px;
}
.dept-editor__hint {
  color: var(--ink-3);
  font-size: 0.9rem;
}
.dept-editor__hint .v-icon {
  color: #d08a12;
  vertical-align: -2px;
}
</style>
